/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-06 11:01:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import {
  convertToShopifyThemeSetting,
  convertToDataThemeSetting,
  updateTranslationPreview,
  checkNewVersionBoostThemeLib
} from 'src/helpers/theme-settings-helper';
import { loadScript, loadScriptWithCallback, removeScript, loadCSS } from 'src/helpers';
import { REACT_DOM_SCRIPT, REACT_SCRIPT } from 'src/constants/integration';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { BUTTON_TYPE_VALUE, ELEMENTS } from 'src/constants/theme-settings';
import BgCollectionHeader from 'src/assets/images/theme-setting/bg-sample.png';

type Props = {
  activeMenuItem: string,
  formValues: any,
  tenantApp: any,
  themeId: string | number,
  themeName: string,
  selectedPage: string,
  labelTranslation: any,
  primaryLocale: string,
  currency: string,
  country: string,
  // preventLoading: boolean,
  boostThemeLib: string,
  boostThemeLibVersion: string,
  themeSettingDefaultValue: any
};

type ComposedProps = Props & WithI18nProps;

class FeLibPreview extends Component<ComposedProps> {
  mainScript = null;
  mainCSS = null;
  vendorScript = null;
  filterScript = null;
  reactScript = null;
  reactDomScript = null;
  loading = false;

  state = {
    stateSetting: null, // Use to check to embed script
    switchingPage: false
  };

  componentDidMount() {
    this.initWindowSetting();
    this.setState({
      stateSetting: window.boostSDAppConfig
    });
  }

  componentWillReceiveProps(nextProps) {
    const { formValues, selectedPage } = this.props;
    const {
      formValues: nextFormValues,
      selectedPage: nextSelectedPage
      // preventLoading
    } = nextProps;

    if (selectedPage?.value !== nextSelectedPage?.value) {
      window.boostSDAppConfig.shop.url = `/${nextSelectedPage.value}`;

      window.boostSDAppConfig.generalSettings.preview_path = `/${nextSelectedPage.value}`;
      window.boostSDAppConfig.generalSettings.page =
        nextSelectedPage.value === 'collections' ? 'collection' : nextSelectedPage.value;

      this.setState({
        switchingPage: true
      });
      this.onLoading();
      this.removeScripts();

      this.timeout = setTimeout(() => {
        this.setState(
          {
            switchingPage: false
          },
          () => {
            this.offLoading();
            this.initScript();
          }
        );
      }, 2000);
    }

    if (!_.isEqual(formValues, nextFormValues)) {
      this.onLoading();
      this.updatePreview(nextProps);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { stateSetting } = this.state;
    const { stateSetting: prevStateSetting } = prevState;
    if (stateSetting !== prevStateSetting && !prevStateSetting) {
      this.initScript();
    }
  }

  componentWillUnmount() {
    // delete window.Shopify;
    // delete window.boostSDAppConfig;
    window.boostSDAppLibWp = [];
    this.removeScripts();
  }

  onLoading = () => {
    if (!this.loading) {
      Emitter.emit(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING);
      this.loading = true;
    }
  };

  offLoading = () => {
    if (this.loading) {
      this.loading = false;
      Emitter.emit(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING);
    }
  };

  initWindowSetting = async () => {
    const {
      tenantApp,
      themeName,
      themeId,
      taeFeatures,
      selectedPage,
      previewSetting,
      primaryLocale,
      currency,
      country
    } = this.props;

    window.Shopify = {
      shop: tenantApp?.tenantId,
      theme: {
        name: themeName,
        id: Number(themeId)
      },
      routes: {
        root: '/'
      },
      country: country,
      currency: {
        active: currency,
        rate: '1.0' // TODO: Check if there is any solution to replace
      }
    };

    if (!_.isEmpty(previewSetting)) {
      if (!previewSetting.generalSettings) {
        previewSetting.generalSettings = {};
      }
      previewSetting.generalSettings.preview_mode = true;
      previewSetting.generalSettings.preview_path = `/${selectedPage.value}`;
      previewSetting.generalSettings.page =
        selectedPage.value === 'collections' ? 'collection' : selectedPage.value;
      previewSetting.generalSettings.current_locale = primaryLocale;
      // filterLayoutCollection and filterLayoutSearch are assumed the same layout
      _.set(
        previewSetting,
        'filterSettings.filterLayout',
        previewSetting?.filterSettings?.filterLayoutCollection
      );
    }

    const shopifyMetafieldSetting = this.getShopifyMetafieldSetting(this.props);
    const host = process.env.REACT_APP_ADMIN_V3_BASE_URL; // 'https://staging.bc-solutions.net'
    window.boostSDAppConfig = {
      api: {
        filterUrl: `${host}/bc-sf-filter/filter`,
        searchUrl: `${host}/bc-sf-filter/search`,
        recommendUrl: `${host}/discovery/recommend`,
        suggestionUrl: `${host}/bc-sf-filter/search/suggest`,
        productsUrl: `${host}/bc-sf-filter/search/products`
      },
      shop: {
        name: tenantApp?.externalInfo?.name,
        url: `/${selectedPage.value}`,
        currency: tenantApp?.externalInfo?.currency,
        money_format: tenantApp?.externalInfo?.money_format,
        money_format_with_currency: tenantApp?.externalInfo?.money_with_currency_format
      },
      ...previewSetting,
      themeSettings: shopifyMetafieldSetting,
      themeInfo: {
        taeFeatures
      }
    };

    window.__BoostCustomization__ = [
      registry => {
        const reloadSettingsPlugin = {
          name: 'Reload ProductItem Theme Settings',
          apply() {
            return {
              afterInit(element) {
                const helpers = element.getHelpers();
                window.addEventListener('reload-theme-settings', () => {
                  helpers.reloadSettings();
                  registry.getModule('Translation').reloadCurrentLanguage();
                });
              }
            };
          }
        };

        const preventClickFromThemeSettings = {
          name: 'Prevent click from Theme settings',
          apply() {
            return {
              afterRender(element) {
                const root = element.getRootElm();

                if (!root) return;
                const clickableElements = root.querySelectorAll('a, button');

                const preventClick = e => {
                  e.preventDefault();
                  e.stopPropagation();
                };

                root.addEventListener('click', preventClick, true);

                clickableElements.forEach(clickableElement => {
                  clickableElement.addEventListener('click', preventClick, true);
                });
              }
            };
          }
        };

        registry.useComponentPlugin('ProductItemThemeSettingsProvider', reloadSettingsPlugin);
        registry.useComponentPlugin('AdditionalElementThemeSettingsProvider', reloadSettingsPlugin);
        registry.useComponentPlugin('ProductListThemeSettingsProvider', reloadSettingsPlugin);
        registry.useComponentPlugin('CollectionHeader', {
          apply() {
            return {
              props(props) {
                props.defaultHeaderImage = BgCollectionHeader;
                props.defaultHeaderDescription =
                  'Give customers details about the banner image(s) or content on the template.';

                return props;
              }
            };
          }
        });

        registry.useComponentPlugin('Sorting', preventClickFromThemeSettings);
        registry.useComponentPlugin('ProductItem', preventClickFromThemeSettings);
        // registry.useComponentPlugin('PaginationButton', preventClickFromThemeSettings);
        // registry.useComponentPlugin('PaginationPageLink', preventClickFromThemeSettings);
      }
    ];
  };

  getShopifyMetafieldSetting = props => {
    const { formValues, themeSettingDefaultValue } = props;

    const themeSetting = _.cloneDeep(formValues);
    if (themeSetting?.cart_setting) {
      themeSetting.cart_setting.enableCart = false; // This preview is built in app-admin
    }

    const truthyFormValues = {};
    Object.keys(themeSetting).forEach(key => {
      if (themeSetting[key]) {
        return (truthyFormValues[key] = themeSetting[key]);
      }
    });

    const transformedData = convertToDataThemeSetting(truthyFormValues);
    const shopMetaFieldSetting = convertToShopifyThemeSetting(
      transformedData,
      themeSettingDefaultValue
    );
    return shopMetaFieldSetting;
  };

  initScript = () => {
    const { boostThemeLib, version } = this.props;
    const host = process.env.REACT_APP_BOOST_CDN_URL;

    // Make sure react load done before load main
    this.reactScript = loadScriptWithCallback(REACT_SCRIPT, () => {
      this.reactDomScript = loadScriptWithCallback(REACT_DOM_SCRIPT, () => {
        this.vendorScript = loadScript(`${host}/theme/${boostThemeLib}/${version}/vendor.js`);
        this.mainScript = loadScript(`${host}/theme/${boostThemeLib}/${version}/main.js`);
      });
    });

    // If version >= 2.0.0 || version = alpha => not load file features => load main.css
    // else version < 2 or version = staging => load file features => support for old theme
    if (checkNewVersionBoostThemeLib(version)) {
      this.mainCSS = loadCSS(`${host}/theme/${boostThemeLib}/${version}/css/main.css`);
    } else this.filterScript = loadScript(`${host}/theme/${boostThemeLib}/${version}/filter.js`);
  };

  removeScripts = () => {
    removeScript(this.mainScript);
    removeScript(this.vendorScript);
    removeScript(this.reactScript);
    removeScript(this.reactDomScript);
    if (checkNewVersionBoostThemeLib(this.props.version)) removeScript(this.mainCSS);
    else removeScript(this.filterScript);
  };

  updatePreview = _.debounce(props => {
    const { labelTranslation, formValues, i18n } = props;
    const shopifyMetafieldSetting = this.getShopifyMetafieldSetting(props);

    this.replaceBase64Image(shopifyMetafieldSetting, formValues);

    window.boostSDAppConfig.themeSettings = shopifyMetafieldSetting;
    // Binding data translation to preview
    const updatedTranslationSetting = updateTranslationPreview(
      formValues,
      labelTranslation,
      window.boostSDAppConfig.translation,
      i18n
    );

    window.boostSDAppConfig.translation = updatedTranslationSetting;

    this.dispatchEventChange();
    this.offLoading();
  }, 1000);

  replaceBase64Image = (shopifyMetafieldSetting, formValues) => {
    const selectOptionBtnKey = Object.keys(formValues).find(key => {
      return (
        key.includes('button') &&
        formValues[key].buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART
      );
    });
    const selectOptionBtnFormValue = formValues[selectOptionBtnKey];
    const selectOptionBtn =
      shopifyMetafieldSetting?.productItems?.productImg?.elements?.selectOptionBtn ||
      shopifyMetafieldSetting?.productItems?.productInfo?.elements?.selectOptionBtn;

    const qvBtnKey = Object.keys(formValues).find(key => {
      return key.includes('button') && formValues[key].buttonType === BUTTON_TYPE_VALUE.QUICK_VIEW;
    });
    const qvBtnFormValue = formValues[qvBtnKey];
    const qvBtn =
      shopifyMetafieldSetting?.productItems?.productImg?.elements?.qvBtn ||
      shopifyMetafieldSetting?.productItems?.productInfo?.elements?.qvBtn;

    const goToDetailBtnKey = Object.keys(formValues).find(key => {
      return (
        key.includes('button') && formValues[key].buttonType === BUTTON_TYPE_VALUE.VIEW_PRODUCT
      );
    });
    const goToDetailBtnFormValue = formValues[goToDetailBtnKey];
    const goToDetailBtn =
      shopifyMetafieldSetting?.productItems?.productImg?.elements?.goToDetailBtn ||
      shopifyMetafieldSetting?.productItems?.productInfo?.elements?.goToDetailBtn;

    if (!_.isEmpty(selectOptionBtn) && selectOptionBtnFormValue?.imgSrc64) {
      _.set(selectOptionBtn, 'imgSrc', selectOptionBtnFormValue.imgSrc64);
    }
    if (!_.isEmpty(qvBtn) && qvBtnFormValue?.imgSrc64) {
      _.set(qvBtn, 'imgSrc', qvBtnFormValue.imgSrc64);
    }
    if (!_.isEmpty(goToDetailBtn) && goToDetailBtnFormValue?.imgSrc64) {
      _.set(goToDetailBtn, 'imgSrc', goToDetailBtnFormValue.imgSrc64);
    }

    const [, nameSaleLabel] = ELEMENTS.PRODUCT_IMAGE_SALE_LABEL.split('_');
    const saleLabelKey = Object.keys(formValues).find(key => {
      return key.includes(nameSaleLabel);
    });
    const saleLabelFormValue = formValues[saleLabelKey];
    const saleLabel = shopifyMetafieldSetting?.productItems?.productImg?.elements?.productSaleLabel;
    if (!_.isEmpty(saleLabel) && saleLabelFormValue?.imgSrc64) {
      _.set(saleLabel, 'imgSrc', saleLabelFormValue.imgSrc64);
    }

    const [, nameSoldOutLabel] = ELEMENTS.PRODUCT_IMAGE_SOLD_OUT_LABEL.split('_');
    const soldOutLabelKey = Object.keys(formValues).find(key => {
      return key.includes(nameSoldOutLabel);
    });
    const soldOutLabelFormValue = formValues[soldOutLabelKey];
    const soldOutLabel =
      shopifyMetafieldSetting?.productItems?.productImg?.elements?.productSoldOutLabel;
    if (!_.isEmpty(soldOutLabel) && soldOutLabelFormValue?.imgSrc64) {
      _.set(soldOutLabel, 'imgSrc', soldOutLabelFormValue.imgSrc64);
    }
  };

  dispatchEventChange = () => {
    const reloadThemeSettingsEvent = new CustomEvent('reload-theme-settings');
    window.dispatchEvent(reloadThemeSettingsEvent);
  };

  render() {
    return <div className="boost-sd__filter-block"></div>;
  }
}

export default withI18n()(FeLibPreview);
